import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import IconAddPerson from '../../components/icons/IconAddPerson';
import WordEditModal from '../students/WordEditModal';
import language from '../../variables/language';
import '../../css/conversations.css';

// "first meaning (+N)" - a word can now have several translations, this
// keeps the table compact rather than dumping the whole list per row.
const translationSummary = (word) => {
   const translations = word.translations || [];
   if (translations.length === 0) return '';
   const first = translations[0].translation;
   return translations.length > 1 ? first + ' (+' + (translations.length - 1) + ')' : first;
}

export default function WordBrowserPanel() {
   const navigate = useNavigate();
   const langRoot = language[language.lang];
   const lang = langRoot.wordsTab;
   const t = langRoot.vocabularyConfigPage;

   const [entries, setEntries] = useState([]);
   const [status, setStatus] = useState('loading');
   const [refresh, setRefresh] = useState(0);
   const [stats, setStats] = useState(null);
   const [search, setSearch] = useState('');
   const [posFilter, setPosFilter] = useState('');
   const [tenseFilter, setTenseFilter] = useState('');
   const [formFilter, setFormFilter] = useState('');
   const [minFrequency, setMinFrequency] = useState('');
   const [maxFrequency, setMaxFrequency] = useState('');
   const [verifiedFilter, setVerifiedFilter] = useState('');
   const [partsOfSpeech, setPartsOfSpeech] = useState([]);
   const [tenses, setTenses] = useState([]);
   const [forms, setForms] = useState([]);
   const [editModalOpen, setEditModalOpen] = useState(false);
   const [editWordId, setEditWordId] = useState(null);
   const [sortKey, setSortKey] = useState('word');
   const [sortDir, setSortDir] = useState('asc');

   const refreshList = () => setRefresh(prev => prev + 1);

   useEffect(() => {
      axios.get('/api/partsofspeech', { withCredentials: true }).then(r => setPartsOfSpeech(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get('/api/tenses', { withCredentials: true }).then(r => setTenses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get('/api/forms', { withCredentials: true }).then(r => setForms(Array.isArray(r.data) ? r.data : [])).catch(() => {});
   }, []);

   useEffect(() => {
      axios.get('/api/words/stats', { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
   }, [refresh]);

   useEffect(() => {
      setStatus('loading');
      const params = { limit: 200 };
      if (search.trim()) params.search = search.trim();
      if (posFilter) params.partOfSpeechId = posFilter;
      if (tenseFilter) params.tenseId = tenseFilter;
      if (formFilter) params.formId = formFilter;
      if (minFrequency !== '') params.minFrequency = minFrequency;
      if (maxFrequency !== '') params.maxFrequency = maxFrequency;
      if (verifiedFilter !== '') params.verified = verifiedFilter;
      const handle = setTimeout(() => {
         axios.get('/api/words/browse', { params, withCredentials: true })
            .then(response => {
               setEntries(Array.isArray(response.data) ? response.data : []);
               setStatus('done');
            })
            .catch(err => {
               if (err.response && err.response.status === 401) navigate('/login');
               setStatus('done');
            });
      }, 300);
      return () => clearTimeout(handle);
   }, [search, posFilter, tenseFilter, formFilter, minFrequency, maxFrequency, verifiedFilter, refresh, navigate]);

   const openCreate = () => {
      setEditWordId(null);
      setEditModalOpen(true);
   }
   const openEdit = (wordId) => {
      setEditWordId(wordId);
      setEditModalOpen(true);
   }
   const closeEdit = () => setEditModalOpen(false);
   const onWordSaved = () => { closeEdit(); refreshList(); };

   const handleSort = (key) => {
      if (sortKey === key) {
         setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
         setSortKey(key);
         setSortDir('asc');
      }
   }

   const columns = useMemo(() => [
      { key: 'word', label: lang.word, type: 'string', accessor: e => e.word.word },
      { key: 'ukTranslation', label: lang.ukTranslation, type: 'string', accessor: e => (e.word.translations && e.word.translations.length ? e.word.translations[0].translation : '') },
      { key: 'partOfSpeech', label: lang.partOfSpeech, type: 'string', accessor: e => (e.word.partOfSpeech ? e.word.partOfSpeech.description : '') },
      { key: 'tense', label: lang.tense, type: 'string', accessor: e => (e.word.tense ? e.word.tense.name : '') },
      { key: 'form', label: lang.form, type: 'string', accessor: e => (e.word.form ? e.word.form.name : '') },
      { key: 'frequency', label: lang.frequency, type: 'number', accessor: e => e.word.frequency },
      { key: 'studentCount', label: t.studentCountHeader, type: 'number', accessor: e => e.studentCount },
   ], [lang, t]);

   const sortedEntries = useMemo(() => {
      const col = columns.find(c => c.key === sortKey);
      if (!col) return entries;
      const withIndex = entries.map((e, i) => ({ e, i }));
      withIndex.sort((a, b) => {
         const av = col.accessor(a.e);
         const bv = col.accessor(b.e);
         let cmp;
         if (col.type === 'number') {
            const an = av == null ? -Infinity : av;
            const bn = bv == null ? -Infinity : bv;
            cmp = an - bn;
         } else {
            cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
         }
         if (cmp === 0) cmp = a.i - b.i;
         return sortDir === 'asc' ? cmp : -cmp;
      });
      return withIndex.map(x => x.e);
   }, [entries, columns, sortKey, sortDir]);

   const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕');

   return (
      <div className="vocab-config-tab-body">
         {stats ? (
            <div className="conv-progress-stats-row">
               <div className="conv-progress-stats">
                  <div className="conv-progress-stat">
                     <span className="conv-progress-stat-value">{stats.total}</span>
                     <span className="conv-progress-stat-label">{t.totalWordsLabel}</span>
                  </div>
                  {stats.byTense.map(tc => (
                     <div className="conv-progress-stat" key={tc.tenseId}>
                        <span className="conv-progress-stat-value">{tc.count}</span>
                        <span className="conv-progress-stat-label">{tc.tenseName}</span>
                     </div>
                  ))}
               </div>
            </div>
         ) : ''}
         <div className="word-browse-toolbar">
            <FilterBar
               search={{ value: search, onChange: setSearch, placeholder: t.searchPlaceholder }}
               filters={[
                  { key: 'pos', type: 'select', label: lang.allPartsOfSpeech, options: partsOfSpeech.map(p => ({ id: p.id, name: p.description })), value: posFilter, onChange: setPosFilter, numeric: true },
                  { key: 'tense', type: 'select', label: lang.allTenses, options: tenses.map(t2 => ({ id: t2.id, name: t2.name })), value: tenseFilter, onChange: setTenseFilter, numeric: true },
                  { key: 'form', type: 'select', label: lang.allForms, options: forms.map(f => ({ id: f.id, name: f.name })), value: formFilter, onChange: setFormFilter, numeric: true },
                  { key: 'verified', type: 'select', label: t.allVerified, options: [{ id: 'true', name: t.verifiedYes }, { id: 'false', name: t.verifiedNo }], value: verifiedFilter, onChange: setVerifiedFilter },
               ]}
               resultCount={entries.length} />
            <label className="word-browse-freq-filter">
               <span>{lang.frequency}</span>
               <input type="number" step="0.1" placeholder="min" value={minFrequency} onChange={e => setMinFrequency(e.target.value)} />
               <i>–</i>
               <input type="number" step="0.1" placeholder="max" value={maxFrequency} onChange={e => setMaxFrequency(e.target.value)} />
            </label>
            <div className="student-lessons-add-btn word-browse-add-btn" onClick={openCreate} title={t.addWord}>
               <IconAdd />
            </div>
         </div>

         <div className="student-lessons-table-wrap">
            <table className="student-lessons-table">
               <thead>
                  <tr>
                     {columns.map(col => (
                        <th key={col.key} className="word-browse-sortable-th" onClick={() => handleSort(col.key)}>
                           {col.label}
                           <span className={'word-browse-sort-indicator' + (sortKey === col.key ? ' active' : '')}>{sortIndicator(col.key)}</span>
                        </th>
                     ))}
                  </tr>
               </thead>
               <tbody>
                  {status === 'loading' ? (
                     <tr className="student-lessons-empty-row"><td colSpan={7}>{langRoot['Loading ...']}</td></tr>
                  ) : ''}
                  {status === 'done' ? sortedEntries.map(entry => (
                     <tr key={entry.word.id} className="student-lessons-row" onClick={() => openEdit(entry.word.id)}>
                        <td className="student-lessons-text-cell">
                           <span className="words-tab-word">{entry.word.word}</span>
                        </td>
                        <td className="student-lessons-text-cell">{translationSummary(entry.word)}</td>
                        <td>{entry.word.partOfSpeech ? entry.word.partOfSpeech.description : ''}</td>
                        <td>{entry.word.tense ? entry.word.tense.name : ''}</td>
                        <td>{entry.word.form ? entry.word.form.name : ''}</td>
                        <td>{entry.word.frequency != null ? entry.word.frequency : ''}</td>
                        <td className="student-lessons-actions-cell" onClick={e => e.stopPropagation()}>
                           <span className="word-browse-student-count" title={t.studentCountHeader}>
                              <IconAddPerson style={{ width: 14, height: 14 }} />
                              {entry.studentCount}
                           </span>
                        </td>
                     </tr>
                  )) : ''}
                  {status === 'done' && entries.length === 0 ? (
                     <tr className="student-lessons-empty-row"><td colSpan={7}>{lang.noWords}</td></tr>
                  ) : ''}
               </tbody>
            </table>
         </div>

         <WordEditModal isOpen={editModalOpen} wordId={editWordId} studentId={undefined}
            onClose={closeEdit} onSaved={onWordSaved} />
      </div>
   );
}
